import { cn } from '@/lib/utils';

export interface Scope<Id extends string> {
  readonly id: Id;
  readonly label: string;
  /**
   * Optional on purpose. Cursor pagination cannot produce a total — see the
   * note in `packages/admin-contract/src/pagination.ts` — so every count on
   * these pills is a second, separate read. A pill renders without its number
   * while that read is in flight or has failed, and the table below never
   * waits on it.
   */
  readonly count?: number;
  /**
   * A dashed marker for a scope that did not exist last time somebody looked.
   * Kept out of the button's accessible name — it is a nicety, and a name that
   * changes when a flag is cleared breaks every lookup that addresses the pill
   * by its label.
   */
  readonly isNew?: boolean;
}

interface ScopePillsProps<Id extends string> {
  /** Names the group for a screen reader; there are several per screen. */
  readonly label: string;
  readonly scopes: readonly Scope<Id>[];
  readonly value: Id;
  readonly onChange: (id: Id) => void;
  readonly className?: string;
}

/**
 * The row of scope filters a list screen opens with.
 *
 * `role="group"` of `aria-pressed` buttons rather than a tablist: these do not
 * swap a panel, they re-filter the one table underneath, and announcing them
 * as tabs would promise a change of context that never comes.
 */
export function ScopePills<Id extends string>({
  label,
  scopes,
  value,
  onChange,
  className,
}: ScopePillsProps<Id>) {
  return (
    <div role="group" aria-label={label} className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {scopes.map((scope) => {
        const isActive = scope.id === value;

        return (
          <button
            key={scope.id}
            type="button"
            aria-pressed={isActive}
            onClick={() => {
              onChange(scope.id);
            }}
            className={cn(
              'inline-flex h-[30px] items-center gap-[7px] rounded-md border px-[11px] text-[13px] transition-colors',
              isActive
                ? 'border-foreground bg-foreground font-semibold text-background'
                : 'border-input bg-card text-muted-foreground hover:text-foreground',
            )}
          >
            {scope.label}
            {scope.count === undefined ? null : (
              <span
                className={cn('font-mono text-xs', isActive ? 'text-background/60' : 'text-muted-foreground')}
              >
                {scope.count.toLocaleString('en-IN')}
              </span>
            )}
            {scope.isNew === true ? (
              <span
                aria-hidden="true"
                className={cn(
                  'inline-flex h-4 items-center rounded-[10px] border border-dashed px-1.5 text-[10.5px] leading-none',
                  isActive ? 'border-background/40 text-background/70' : 'border-input text-muted-foreground',
                )}
              >
                new
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
